import React, { useState, useEffect } from 'react';
import ShopifyService from '../services/shopifyService';
import VendorBulkPriceUpdate from '../components/VendorBulkPriceUpdate';

const ProductSearchPage = () => {
  const [activeTab, setActiveTab] = useState('search');
  const [searchType, setSearchType] = useState('vendor');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Vendor state'leri
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [vendorProducts, setVendorProducts] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [allVendorProducts, setAllVendorProducts] = useState([]);
  
  // Ürün düzenleme state'leri
  const [editingProduct, setEditingProduct] = useState(null);
  const [productUpdates, setProductUpdates] = useState({});
  const [variantUpdates, setVariantUpdates] = useState({});
  const [saving, setSaving] = useState(false);

  // Vendor'ları yükle
  useEffect(() => {
    const loadVendors = async () => {
      try {
        const result = await ShopifyService.getAllVendors();
        if (result.success) {
          setVendors(result.vendors);
        }
      } catch (error) {
        console.error('Vendor yükleme hatası:', error);
      }
    };
    
    loadVendors();
  }, []);

  // Vendor seçildiğinde TÜM ürünleri getir
  const handleVendorSelect = async (vendor) => {
    if (!vendor) {
      setSelectedVendor('');
      setVendorProducts([]);
      setAllVendorProducts([]);
      return;
    }
    
    setSelectedVendor(vendor);
    setLoading(true);
    setError('');
    setSearchResults(null);
    setVendorProducts([]);
    setAllVendorProducts([]);
    setEditingProduct(null);
    
    try {
      // Tüm status'lerdeki ürünleri getir
      const result = await ShopifyService.getProductsByVendor(vendor, 'all');
      
      if (result.success) {
        setAllVendorProducts(result.products);
        // Local filtreleme uygula
        filterVendorProducts(result.products, statusFilter);
      } else {
        setError(result.message || 'Vendor ürünleri getirilemedi');
      }
    } catch (err) {
      setError('Vendor ürünleri getirme hatası: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Local filtreleme fonksiyonu
  const filterVendorProducts = (products, filter) => {
    if (filter === 'all') {
      setVendorProducts(products);
    } else {
      const filtered = products.filter(product => product.status === filter);
      setVendorProducts(filtered);
    }
  };

  // Status filter - LOCAL filtreleme
  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    if (selectedVendor && allVendorProducts.length > 0) {
      filterVendorProducts(allVendorProducts, newStatus);
    }
  };

  // SKU/ID ile ürün ara
  const handleSearch = async () => {
    if (!searchValue.trim()) {
      setError('Lütfen arama değeri girin');
      return;
    }

    setLoading(true);
    setError('');
    setSearchResults(null);
    setVendorProducts([]);
    setSelectedVendor('');
    setEditingProduct(null);

    try {
      let result;
      if (searchType === 'sku') {
        result = await ShopifyService.searchProductBySku(searchValue);
      } else {
        result = await ShopifyService.getProductById(parseInt(searchValue));
      }

      if (result.success) {
        setSearchResults(result.data);
        initializeEditMode(result.data);
      } else {
        setError(result.message || 'Ürün bulunamadı');
      }
    } catch (err) {
      setError('Arama hatası: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Ürün seç ve düzenleme moduna geç
  const handleProductSelect = (product) => {
    setSearchResults(product);
    initializeEditMode(product);
  };

  // 🎯 YENİ: Düzenleme modunu başlat - COST PER ITEM EKLENDİ
  const initializeEditMode = (product) => {
    setEditingProduct(product);
    setProductUpdates({
      status: product.status || 'active'
    });
    
    // Variant updates'i başlat - COST PER ITEM EKLENDİ
    const initialVariantUpdates = {};
    if (product.variants) {
      product.variants.forEach(variant => {
        initialVariantUpdates[variant.id] = {
          price: variant.price || '',
          cost_per_item: variant.cost_per_item || '',
          inventory_quantity: variant.inventory_quantity || variant.inventoryQuantity || 0
        };
      });
    }
    setVariantUpdates(initialVariantUpdates);
  };

  // Ürün durumu değişikliği
  const handleStatusChange = (newStatus) => {
    setProductUpdates(prev => ({
      ...prev,
      status: newStatus
    }));
  };

  // Variant fiyat değişikliği
  const handleVariantPriceChange = (variantId, newPrice) => {
    setVariantUpdates(prev => ({
      ...prev,
      [variantId]: {
        ...prev[variantId],
        price: newPrice
      }
    }));
  };

  // 🎯 YENİ: Variant cost değişikliği
  const handleVariantCostChange = (variantId, newCost) => {
    setVariantUpdates(prev => ({
      ...prev,
      [variantId]: {
        ...prev[variantId],
        cost_per_item: newCost
      }
    }));
  };

  // Variant stok değişikliği
  const handleVariantInventoryChange = (variantId, newQuantity) => {
    setVariantUpdates(prev => ({
      ...prev,
      [variantId]: {
        ...prev[variantId],
        inventory_quantity: parseInt(newQuantity) || 0
      }
    }));
  };

  // 🎯 YENİ: Tekil güncelleme - Sadece fiyat
  const handleUpdatePriceOnly = async (variantId, newPrice) => {
    if (!newPrice) {
      setError('Lütfen fiyat girin');
      return;
    }

    try {
      setSaving(true);
      const result = await ShopifyService.updateProductPrice(variantId, newPrice);
      
      if (result.success) {
        alert('✅ Fiyat güncellendi!');
        
        // State'i güncelle
        setSearchResults(prev => ({
          ...prev,
          variants: prev.variants.map(variant => 
            variant.id === variantId 
              ? { ...variant, price: newPrice }
              : variant
          )
        }));

        setEditingProduct(prev => ({
          ...prev,
          variants: prev.variants.map(variant => 
            variant.id === variantId 
              ? { ...variant, price: newPrice }
              : variant
          )
        }));

        // Variant updates'ten bu variantı kaldır
        setVariantUpdates(prev => {
          const newUpdates = { ...prev };
          delete newUpdates[variantId];
          return newUpdates;
        });

      } else {
        setError('Fiyat güncelleme hatası: ' + result.message);
      }
    } catch (err) {
      setError('Fiyat güncelleme hatası: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // 🎯 YENİ: Tekil güncelleme - Sadece cost
  const handleUpdateCostOnly = async (variantId, newCost) => {
    if (!newCost) {
      setError('Lütfen maliyet girin');
      return;
    }

    try {
      setSaving(true);
      const result = await ShopifyService.updateVariantCostPrice(variantId, newCost);
      
      if (result.success) {
        alert('✅ Maliyet güncellendi!');
        
        // State'i güncelle
        setSearchResults(prev => ({
          ...prev,
          variants: prev.variants.map(variant => 
            variant.id === variantId 
              ? { ...variant, cost_per_item: newCost }
              : variant
          )
        }));

        setEditingProduct(prev => ({
          ...prev,
          variants: prev.variants.map(variant => 
            variant.id === variantId 
              ? { ...variant, cost_per_item: newCost }
              : variant
          )
        }));

        // Variant updates'ten bu variantı kaldır
        setVariantUpdates(prev => {
          const newUpdates = { ...prev };
          delete newUpdates[variantId];
          return newUpdates;
        });

      } else {
        setError('Maliyet güncelleme hatası: ' + result.message);
      }
    } catch (err) {
      setError('Maliyet güncelleme hatası: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Değişiklikleri kaydet
  const handleSaveChanges = async () => {
    if (!editingProduct) return;

    setSaving(true);
    setError('');

    try {
      const updates = {
        productId: editingProduct.id,
        status: productUpdates.status,
        variants: Object.entries(variantUpdates).map(([variantId, updates]) => ({
          id: parseInt(variantId),
          ...updates
        }))
      };

      const result = await ShopifyService.updateProductBulk(updates);
      
      if (result.success) {
        alert('✅ Değişiklikler başarıyla kaydedildi!');
        
        // State'i güncelle
        setSearchResults(prev => ({
          ...prev,
          status: productUpdates.status,
          variants: prev.variants.map(variant => {
            const updatedVariant = variantUpdates[variant.id];
            if (updatedVariant) {
              return {
                ...variant,
                price: updatedVariant.price || variant.price,
                cost_per_item: updatedVariant.cost_per_item || variant.cost_per_item,
                inventory_quantity: updatedVariant.inventory_quantity ?? variant.inventory_quantity
              };
            }
            return variant;
          })
        }));

        setEditingProduct(prev => ({
          ...prev,
          status: productUpdates.status,
          variants: prev.variants.map(variant => {
            const updatedVariant = variantUpdates[variant.id];
            if (updatedVariant) {
              return {
                ...variant,
                price: updatedVariant.price || variant.price,
                cost_per_item: updatedVariant.cost_per_item || variant.cost_per_item,
                inventory_quantity: updatedVariant.inventory_quantity ?? variant.inventory_quantity
              };
            }
            return variant;
          })
        }));

        // Variant updates'i sıfırla
        setVariantUpdates({});

      } else {
        setError('Kaydetme hatası: ' + result.message);
      }
    } catch (err) {
      setError('Kaydetme hatası: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Değişiklik var mı kontrolü
  const hasChanges = () => {
    if (!editingProduct) return false;

    // Ürün durumu değişmiş mi?
    const statusChanged = productUpdates.status !== editingProduct.status;
    
    // Variant değişiklikleri var mı?
    const variantChanges = Object.entries(variantUpdates).some(([variantId, updates]) => {
      const originalVariant = editingProduct.variants?.find(v => v.id === parseInt(variantId));
      if (!originalVariant) return false;
      
      return updates.price !== originalVariant.price?.toString() ||
             updates.cost_per_item !== originalVariant.cost_per_item?.toString() ||
             updates.inventory_quantity !== originalVariant.inventory_quantity;
    });

    return statusChanged || variantChanges;
  };

  // Gerçek zamanlı değer gösterimi için yardımcı fonksiyon
  const getDisplayValue = (variant, field) => {
    if (variantUpdates[variant.id] && variantUpdates[variant.id][field] !== undefined) {
      return variantUpdates[variant.id][field];
    }
    return variant[field];
  };

  // Değişiklik renkleri
  const getFieldStyle = (variant, field) => {
    const hasChange = variantUpdates[variant.id] && 
                     variantUpdates[variant.id][field] !== undefined &&
                     variantUpdates[variant.id][field] !== variant[field];
    
    if (hasChange) {
      return {
        backgroundColor: '#fff3cd',
        border: '2px solid #ffc107',
        fontWeight: 'bold'
      };
    }
    
    return {
      backgroundColor: 'white',
      border: '1px solid #ddd'
    };
  };

  // Yardımcı fonksiyonlar
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'draft': return '#ffc107';
      case 'archived': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return '🟢';
      case 'draft': return '🟡';
      case 'archived': return '🔴';
      default: return '⚪';
    }
  };

  // 🎯 TAB CONTENT - Ürün Arama Kısmı
  const renderProductSearchTab = () => (
    <div>
      <h2 style={{ color: '#1976d2', marginBottom: '1rem' }}>🔍 Ürün Arama ve Düzenleme</h2>

      {/* Debug Bilgisi */}
      <div style={{ 
        padding: '1rem', 
        backgroundColor: '#e3f2fd', 
        borderRadius: '8px',
        marginBottom: '1rem',
        fontSize: '0.9rem'
      }}>
        <strong>🏪 Vendor Bilgisi:</strong> {vendors.length} vendor yüklendi | 
        <strong> Seçili:</strong> {selectedVendor || 'Yok'} | 
        <strong> Filtre:</strong> {statusFilter}
      </div>

      {/* Arama Formu */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '1.5rem', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Arama Türü:
            </label>
            <select 
              value={searchType} 
              onChange={(e) => {
                setSearchType(e.target.value);
                setSearchValue('');
                setSelectedVendor('');
                setVendorProducts([]);
                setSearchResults(null);
                setEditingProduct(null);
              }}
              style={{ 
                padding: '0.5rem', 
                border: '1px solid #ddd', 
                borderRadius: '4px',
                minWidth: '120px'
              }}
            >
              <option value="vendor">Vendor</option>
              <option value="sku">SKU</option>
              <option value="id">ID</option>
            </select>
          </div>
          
          {/* Vendor Dropdown */}
          {searchType === 'vendor' ? (
            <div style={{ flex: 1, minWidth: '300px' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Vendor Seçin:
              </label>
              <select 
                value={selectedVendor}
                onChange={(e) => handleVendorSelect(e.target.value)}
                disabled={loading || vendors.length === 0}
                style={{ 
                  padding: '0.5rem', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  width: '100%'
                }}
              >
                <option value="">Vendor seçin... ({vendors.length} vendor)</option>
                {vendors.map((vendor, index) => (
                  <option key={index} value={vendor}>
                    {vendor}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            /* SKU/ID Input */
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                {searchType === 'sku' ? 'SKU Değeri:' : 'Ürün ID:'}
              </label>
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={searchType === 'sku' ? 'ABC123...' : '12345...'}
                style={{ 
                  padding: '0.5rem', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  width: '100%'
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
          )}
          
          {searchType !== 'vendor' && (
            <button 
              onClick={handleSearch}
              disabled={loading || !searchValue.trim()}
              style={{ 
                padding: '0.5rem 1.5rem', 
                backgroundColor: '#1976d2', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: (loading || !searchValue.trim()) ? 'not-allowed' : 'pointer',
                opacity: (loading || !searchValue.trim()) ? 0.6 : 1
              }}
            >
              {loading ? '🔍 Aranıyor...' : '🔍 Ara'}
            </button>
          )}
        </div>
      </div>

      {/* Status Filter - Sadece vendor seçiliyse göster */}
      {selectedVendor && allVendorProducts.length > 0 && (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '1rem', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
          display: 'flex',
          gap: '1rem',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: 'bold' }}>📊 Durum Filtresi:</span>
          
          {['all', 'active', 'draft', 'archived'].map(status => {
            const count = status === 'all' 
              ? allVendorProducts.length 
              : allVendorProducts.filter(p => p.status === status).length;
            
            const isActive = statusFilter === status;
            
            return (
              <button 
                key={status}
                onClick={() => handleStatusFilterChange(status)}
                disabled={count === 0}
                style={{ 
                  padding: '0.5rem 1rem', 
                  backgroundColor: isActive ? getStatusColor(status) : '#f8f9fa', 
                  color: isActive ? 'white' : '#333',
                  border: `1px solid ${getStatusColor(status)}`,
                  borderRadius: '4px',
                  cursor: count === 0 ? 'not-allowed' : 'pointer',
                  opacity: count === 0 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {getStatusIcon(status)} {status === 'all' ? 'Tümü' : status} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Hata Mesajı */}
      {error && (
        <div style={{ 
          padding: '1rem', 
          backgroundColor: '#f8d7da', 
          color: '#721c24',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Vendor Ürün Listesi */}
      {selectedVendor && (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '1.5rem', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem'
        }}>
          <h3 style={{ color: '#1976d2', marginBottom: '1rem' }}>
            🏪 {selectedVendor} - {vendorProducts.length} Ürün
            <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '1rem' }}>
              ({statusFilter !== 'all' ? `${statusFilter} filtresi` : 'tüm ürünler'})
            </span>
          </h3>
          
          {vendorProducts.length > 0 ? (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {vendorProducts.map((product) => (
                <div 
                  key={product.id}
                  style={{ 
                    padding: '1rem', 
                    border: `2px solid ${getStatusColor(product.status)}`, 
                    borderRadius: '8px',
                    backgroundColor: '#fafafa',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#fafafa'}
                  onClick={() => handleProductSelect(product)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>
                        {product.title}
                      </h4>
                      <div style={{ fontSize: '0.8rem', color: '#666', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        <span><strong>ID:</strong> {product.id}</span>
                        <span>
                          <strong>Status:</strong> 
                          <span style={{ 
                            color: getStatusColor(product.status),
                            fontWeight: 'bold',
                            marginLeft: '0.25rem'
                          }}>
                            {getStatusIcon(product.status)} {product.status}
                          </span>
                        </span>
                        <span><strong>Vendor:</strong> {product.vendor || 'N/A'}</span>
                        <span><strong>Varyant:</strong> {product.variants?.length || 0} adet</span>
                      </div>
                    </div>
                    <div style={{ 
                      padding: '0.5rem 1rem', 
                      backgroundColor: getStatusColor(product.status),
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      minWidth: '80px',
                      textAlign: 'center'
                    }}>
                      {getStatusIcon(product.status)} {product.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ 
              padding: '2rem', 
              textAlign: 'center', 
              color: '#666',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <p>📭 "{selectedVendor}" vendor'ına ait {statusFilter !== 'all' ? statusFilter : ''} ürün bulunamadı.</p>
            </div>
          )}
        </div>
      )}

      {/* Ürün Düzenleme Ekranı */}
      {searchResults && editingProduct && (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '1.5rem', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: '#1976d2', margin: 0 }}>✏️ Ürün Düzenleme</h3>
            {hasChanges() && (
              <span style={{ 
                padding: '0.25rem 0.75rem', 
                backgroundColor: '#ffc107', 
                color: '#333',
                borderRadius: '4px',
                fontSize: '0.8rem',
                fontWeight: 'bold'
              }}>
                ⚠️ Kaydedilmemiş Değişiklikler
              </span>
            )}
          </div>
          
          {/* Ürün Bilgileri */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '1px solid #eee' }}>
            <div style={{ flex: 1 }}>
              <h4 style={{ color: '#1976d2', margin: '0 0 0.5rem 0' }}>
                {searchResults.title}
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem', fontSize: '0.9rem' }}>
                <p style={{ margin: '0.25rem 0', color: '#666' }}>
                  <strong>ID:</strong> {searchResults.id}
                </p>
                <p style={{ margin: '0.25rem 0', color: '#666' }}>
                  <strong>Vendor:</strong> {searchResults.vendor || 'N/A'}
                </p>
                <p style={{ margin: '0.25rem 0', color: '#666' }}>
                  <strong>Status:</strong> 
                  <span style={{ 
                    color: getStatusColor(searchResults.status),
                    fontWeight: 'bold',
                    marginLeft: '0.25rem'
                  }}>
                    {getStatusIcon(searchResults.status)} {searchResults.status}
                  </span>
                </p>
              </div>
            </div>
            
            {/* Ürün Durumu Düzenleme */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Ürün Durumu:
              </label>
              <select 
                value={productUpdates.status} 
                onChange={(e) => handleStatusChange(e.target.value)}
                style={{ 
                  padding: '0.5rem', 
                  border: productUpdates.status !== editingProduct.status ? '2px solid #ffc107' : '1px solid #ddd', 
                  borderRadius: '4px',
                  backgroundColor: productUpdates.status === 'active' ? '#e8f5e8' : 
                                 productUpdates.status === 'draft' ? '#fff3cd' : '#f8d7da',
                  fontWeight: productUpdates.status !== editingProduct.status ? 'bold' : 'normal',
                  minWidth: '120px'
                }}
              >
                <option value="active">🟢 Active</option>
                <option value="draft">🟡 Draft</option>
                <option value="archived">🔴 Archived</option>
              </select>
            </div>
          </div>

          {/* Variant Listesi */}
          <div>
            <h4 style={{ color: '#333', marginBottom: '1rem' }}>📦 Variantlar ({searchResults.variants?.length || 0})</h4>
            
            {searchResults.variants && searchResults.variants.length > 0 ? (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {searchResults.variants.map((variant) => (
                  <div 
                    key={variant.id}
                    style={{ 
                      padding: '1rem', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: '8px',
                      backgroundColor: '#fafafa'
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '1rem', alignItems: 'center' }}>
                      {/* Variant Bilgisi */}
                      <div>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                          {variant.title === 'Default Title' ? 'Ana Ürün' : variant.title}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                          ID: {variant.id} {variant.sku && `| SKU: ${variant.sku}`}
                        </div>
                      </div>
                      
                      {/* Satış Fiyatı */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.8rem', fontWeight: 'bold' }}>
                          Satış Fiyatı:
                        </label>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={getDisplayValue(variant, 'price')}
                            onChange={(e) => handleVariantPriceChange(variant.id, e.target.value)}
                            style={{ 
                              padding: '0.25rem 0.5rem', 
                              borderRadius: '4px',
                              width: '80px',
                              ...getFieldStyle(variant, 'price')
                            }}
                            placeholder="0.00"
                          />
                          <span>USD</span>
                        </div>
                      </div>
                      
                      {/* 🎯 YENİ: Maliyet Fiyatı */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.8rem', fontWeight: 'bold' }}>
                          Maliyet:
                        </label>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={getDisplayValue(variant, 'cost_per_item')}
                            onChange={(e) => handleVariantCostChange(variant.id, e.target.value)}
                            style={{ 
                              padding: '0.25rem 0.5rem', 
                              borderRadius: '4px',
                              width: '80px',
                              ...getFieldStyle(variant, 'cost_per_item')
                            }}
                            placeholder="0.00"
                          />
                          <span>USD</span>
                        </div>
                      </div>
                      
                      {/* Stok Miktarı */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.8rem', fontWeight: 'bold' }}>
                          Stok:
                        </label>
                        <input
                          type="number"
                          value={getDisplayValue(variant, 'inventory_quantity')}
                          onChange={(e) => handleVariantInventoryChange(variant.id, e.target.value)}
                          min="0"
                          style={{ 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px',
                            width: '80px',
                            ...getFieldStyle(variant, 'inventory_quantity')
                          }}
                        />
                      </div>
                      
                      {/* Hızlı Güncelleme Butonları */}
                      <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                        <button
                          onClick={() => handleUpdatePriceOnly(variant.id, variantUpdates[variant.id]?.price)}
                          disabled={saving || !variantUpdates[variant.id]?.price}
                          style={{ 
                            padding: '0.25rem 0.5rem', 
                            backgroundColor: variantUpdates[variant.id]?.price ? '#28a745' : '#6c757d', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            cursor: (saving || !variantUpdates[variant.id]?.price) ? 'not-allowed' : 'pointer'
                          }}
                          title="Sadece satış fiyatını güncelle"
                        >
                          {saving ? '⏳' : '💰 Fiyat'}
                        </button>
                        
                        {/* 🎯 YENİ: Maliyet Güncelleme Butonu */}
                        <button
                          onClick={() => handleUpdateCostOnly(variant.id, variantUpdates[variant.id]?.cost_per_item)}
                          disabled={saving || !variantUpdates[variant.id]?.cost_per_item}
                          style={{ 
                            padding: '0.25rem 0.5rem', 
                            backgroundColor: variantUpdates[variant.id]?.cost_per_item ? '#17a2b8' : '#6c757d', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            cursor: (saving || !variantUpdates[variant.id]?.cost_per_item) ? 'not-allowed' : 'pointer'
                          }}
                          title="Sadece maliyeti güncelle"
                        >
                          {saving ? '⏳' : '📊 Maliyet'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '2rem' }}>
                Bu ürüne ait varyant bulunamadı.
              </p>
            )}
          </div>

          {/* Kaydetme Butonu */}
          {hasChanges() && (
            <div style={{ 
              marginTop: '2rem', 
              paddingTop: '1rem', 
              borderTop: '2px solid #1976d2',
              textAlign: 'center'
            }}>
              <button 
                onClick={handleSaveChanges}
                disabled={saving}
                style={{ 
                  padding: '0.75rem 2rem', 
                  backgroundColor: '#1976d2', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                  minWidth: '200px'
                }}
              >
                {saving ? '⏳ Kaydediliyor...' : '💾 Tüm Değişiklikleri Kaydet'}
              </button>
              <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                Ürün durumu ve tüm varyant değişiklikleri kaydedilecek
              </p>
            </div>
          )}
        </div>
      )}

      {/* Yükleme Durumu */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>🔄 Yükleniyor...</div>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>
            {selectedVendor ? `${selectedVendor} ürünleri getiriliyor...` : 'İşlem yapılıyor...'}
          </div>
        </div>
      )}
    </div>
  );

  // 🎯 ANA RETURN - TAB YAPISI
  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      
      {/* TAB NAVIGATION */}
      <div style={{ marginBottom: '2rem', borderBottom: '1px solid #ddd' }}>
        <button 
          onClick={() => setActiveTab('search')}
          style={{ 
            padding: '1rem 2rem',
            backgroundColor: activeTab === 'search' ? '#1976d2' : 'transparent',
            color: activeTab === 'search' ? 'white' : '#1976d2',
            border: 'none',
            borderBottom: activeTab === 'search' ? '3px solid #1976d2' : 'none',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 'bold'
          }}
        >
          🔍 Ürün Arama
        </button>
        <button 
          onClick={() => setActiveTab('bulk-update')}
          style={{ 
            padding: '1rem 2rem',
            backgroundColor: activeTab === 'bulk-update' ? '#1976d2' : 'transparent',
            color: activeTab === 'bulk-update' ? 'white' : '#1976d2',
            border: 'none',
            borderBottom: activeTab === 'bulk-update' ? '3px solid #1976d2' : 'none',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 'bold'
          }}
        >
          🏪 Toplu Fiyat Güncelleme
        </button>
      </div>

      {/* TAB CONTENT */}
      {activeTab === 'search' ? (
        renderProductSearchTab()
      ) : (
        <VendorBulkPriceUpdate />
      )}
    </div>
  );
};

export default ProductSearchPage;